// Import Types
import type {
    AdministrationSettingConfigurationItem,
    AdministrationSettingItem,
    DataWithPaging,
    AdministrationPartnersListItem,
    ActivePartner,
    PartnerSubscription,
    Subscription,
} from 'types';
// Import Configs
import { CRMRequestService, postCancelableRequest, getCancelableRequest } from './http.config';

const AdministrationHttpService = {
    // CJBlockManagement requests
    getCustomerJourneyBlocks: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => 'Admin/Workflow/BlockList',
        });
    },
    addPartnerToCJBlock: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => 'Admin/Workflow/AddPartnerBlock',
        });
    },
    getCJBlockPartners: () => {
        return getCancelableRequest<number[]>({
            requestService: CRMRequestService,
            endpointPath: ({ blockId }) => `Admin/Workflow/GetBlockPartners?blockId=${blockId}`,
        });
    },
    removePartnersFromCJBlock: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => 'Admin/Workflow/RemovePartnersBlock',
        });
    },
    // Settings requests
    getSettingList: () => {
        return postCancelableRequest<DataWithPaging<AdministrationSettingItem>>({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/Setting/List',
        });
    },
    getSettingConfigurations: () => {
        return getCancelableRequest<DataWithPaging<AdministrationSettingConfigurationItem>>({
            requestService: CRMRequestService,
            endpointPath: () => 'Admin/Setting/Configurations',
        });
    },
    createSetting: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/Setting/Create',
        });
    },
    updateSetting: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/Setting/Update',
        });
    },
    deleteSetting: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/Setting/Delete',
        });
    },
    getEmailProviderList: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/EmailProvider/List',
        });
    },
    setEmailProvider: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/EmailProvider/Create',
        });
    },
    updateEmailProvider: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/EmailProvider/Update',
        });
    },
    getEmailProvider: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ providerId }) => `/Admin/EmailProvider/Get?emailProviderId=${providerId}`,
        });
    },
    getSMSProvidersList: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/SmsProvider/List',
        });
    },
    getSMSProvider: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ smsProviderId }) => `/Admin/SmsProvider/Get?smsProviderId=${smsProviderId}`,
        });
    },
    createSMSProvider: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/SmsProvider/Create',
        });
    },
    updateSMSProvider: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/SmsProvider/Update',
        });
    },
    testSendSMSProvider: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ to }) => `/Admin/SmsProvider/TestSend?to=${to}`,
        });
    },
    getPartnerList: () => {
        return postCancelableRequest<DataWithPaging<AdministrationPartnersListItem>>({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/Partner/List',
        });
    },
    getPartnersFullList: () => {
        return getCancelableRequest<ActivePartner[]>({
            requestService: CRMRequestService,
            endpointPath: () => 'Admin/Partner/GetPartners',
        });
    },
    getPartnerGroups: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/Partner/GetPartnerGroups',
        });
    },
    updatePartnerSubscription: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/Partner/UpdatePartnerSubscription',
        });
    },
    getAllSubscriptions: () => {
        return getCancelableRequest<Subscription[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/Partner/GetAllSubscriptions',
        });
    },
    getPartnerSubscriptions: () => {
        return getCancelableRequest<PartnerSubscription[], { partnerId: number }>({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/Admin/Partner/GetPartnerSubscriptions?partnerId=${partnerId}`,
        });
    },
    // DataWarehouse requests
    getDBSchema: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/Admin/Schema/List?partnerId=${partnerId}`,
        });
    },
    updateDBRowSchema: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/Admin/Schema/Update?partnerId=${partnerId}`,
        });
    },
    getClassifiersList: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/Admin/Classifier/List?partnerId=${partnerId}`,
        });
    },
    createClassifier: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/Admin/Classifier/Create?partnerId=${partnerId}`,
        });
    },
    deleteClassifier: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId, classifierId }) =>
                `/Admin/Classifier/Delete?partnerId=${partnerId}&classifierId=${classifierId}`,
        });
    },
    updateClassifier: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/Admin/Classifier/Update?partnerId=${partnerId}`,
        });
    },
    getClassifier: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId, classifierId }) =>
                `/Admin/Classifier/GetById?partnerId=${partnerId}&classifierId=${classifierId}`,
        });
    },
    getMenuItemsList: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/Admin/MenuItem/List?partnerId=${partnerId}`,
        });
    },
    getMenuItem: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId, menuItemId }) =>
                `/Admin/MenuItem/GetById?partnerId=${partnerId}&menuItemId=${menuItemId}`,
        });
    },
    createMenuItem: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/Admin/MenuItem/Create?partnerId=${partnerId}`,
        });
    },
    deleteMenuItem: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId, menuItemId }) =>
                `/Admin/MenuItem/Delete?partnerId=${partnerId}&menuItemId=${menuItemId}`,
        });
    },
    updateMenuItem: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/Admin/MenuItem/Update?partnerId=${partnerId}`,
        });
    },
    getUsersList: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Admin/User/List`,
        });
    },
    eventLogList: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Admin/EventLog/List',
        });
    },
};

export default AdministrationHttpService;
